import { useCallback, useState } from 'react';
import { useTheme } from '@/hooks/useTheme';
import { useTenantHostCache } from '@/features/tenant-config/hooks/useTenantByHost';

/** Bundled marks, keyed by the surface they sit on rather than their own ink. */
const PLATFORM_LOGO = {
  light: '/minetech-logo-dark.png',
  dark: '/minetech-logo-light.png',
} as const;

/**
 * The backend's fallback for `branding.logoUrl`. Getting it back means the
 * tenant has configured nothing, which is the only case where the light/dark
 * pair applies — a tenant supplies one mark and it is used on both surfaces.
 */
const PLATFORM_FALLBACK: string = PLATFORM_LOGO.light;

type Surface = 'light' | 'dark';

/**
 * FR6 — the workspace logo, with the platform mark as its fallback.
 *
 * Pass a `surface` for a logo sitting on a fixed background, such as the
 * sign-in hero over its photo; omit it to follow the user's theme.
 */
export function useTenantLogo(surface?: Surface) {
  // Reads the cache only. The boot gate owns the request; see useTenantHostCache.
  const data = useTenantHostCache();
  const { resolvedTheme } = useTheme();
  const [brokenSrc, setBrokenSrc] = useState<string | null>(null);

  const configured = data?.branding?.['branding.logoUrl'];
  const tenantLogo =
    configured && configured !== PLATFORM_FALLBACK && configured !== brokenSrc
      ? configured
      : null;

  // A tenant's URL can rot without anyone noticing. Falling back beats an alt
  // slug where the logo should be.
  const onError = useCallback(
    () => setBrokenSrc(configured ?? null),
    [configured],
  );

  return {
    src: tenantLogo ?? PLATFORM_LOGO[surface ?? resolvedTheme],
    alt: tenantLogo ? (data?.name ?? 'Workspace logo') : 'MineTech',
    /**
     * The bundled marks are wordmarks with a lot of built-in whitespace, and
     * every call site crops that away with an oversized height inside a short
     * overflow-hidden box. A tenant's logo has no such padding and is usually
     * square, so it must be sized plainly instead — callers branch on this.
     */
    isCustom: tenantLogo !== null,
    /** The workspace name, for screens that identify the tenant in words. */
    workspaceName: data?.name ?? null,
    onError,
  };
}
